/**
 * The custom Quill blots that make the schema's embeds real objects in the
 * editor.
 *
 * WHY BLOTS AT ALL. A merge field written as the literal text
 * `{{signer_name}}` can be broken by a single backspace in the middle of it,
 * leaving `{{signer_nam}}` — a token that resolves to nothing, in a document
 * somebody signs. As an EMBED it is one atomic object: Quill selects it,
 * moves it and deletes it like a single character, and it cannot be
 * half-edited into an unresolvable state.
 *
 * WHAT QUILL DOES WITH THESE. `getContents()` emits an embed op shaped
 * `{ insert: { <blotName>: <static value(node)> } }`. That is exactly the
 * shape `DeltaEmbedSchema` validates, so the blot names below are not free
 * choices — each one IS a schema key, and renaming one silently produces a
 * document the store rejects.
 *
 * `Quill` is passed IN rather than imported. This package has no
 * dependencies and no package.json (see `index.ts`); the consuming app owns
 * `react-quill-new` and hands us its Quill so there is exactly one Quill
 * instance and one blot registry in the bundle.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Set once registration has run, so a re-render never re-registers. */
let registered = false;

/* ── DOM helpers ────────────────────────────────────────────────────────── */

function el(tag: string, className?: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * The colour group for a field name. Kept as a PREFIX rule rather than a
 * catalog lookup so a blot can render without the catalog being loaded —
 * a chip must never fail to draw because a lazy import has not resolved.
 * An unknown prefix falls back to the neutral tint, never to an error state:
 * an operator-added field is not a defect.
 */
function groupClassFor(name: string): string {
  if (name.startsWith('company_')) return 'dq-g-company';
  if (name.startsWith('customer_')) return 'dq-g-customer';
  if (name.startsWith('signer_') || name.startsWith('guarantor_')) return 'dq-g-signer';
  if (name.startsWith('device_') || name.startsWith('finance_') || name.startsWith('price_')) return 'dq-g-finance';
  if (name.startsWith('agreement_')) return 'dq-g-agree';
  return 'dq-g-other';
}

/**
 * A field's human label, when the host has supplied the catalog.
 *
 * The chip shows "Signer name", not `{{signer_name}}` — the label is what an
 * author reads a contract with. Falls back to the token so a field that is no
 * longer in the catalog is still VISIBLE and removable rather than blank.
 */
let labelLookup: (name: string) => string | undefined = () => undefined;

/**
 * COMMIT A TABLE'S NEW VALUE THROUGH QUILL, not around it.
 *
 * Quill CACHES its document delta and only invalidates that cache when it is
 * told the document changed. Writing a new value into the embed's
 * `data-table` attribute and calling `getContents()` therefore returned the
 * STALE table — the edit was in the DOM, absent from the model, and the next
 * render put the old table back on screen. That is how a filled-in table
 * silently emptied itself.
 *
 * So the editor supplies a hook that replaces the embed through Quill's own
 * API. That invalidates the cache, fires `text-change`, and puts the edit in
 * the undo stack, which the attribute-only route never could.
 */
let tableApplyHook: (node: HTMLElement, next: unknown) => void = () => {};

export function setTableApplyHook(
  fn: (node: HTMLElement, next: unknown) => void,
): void {
  tableApplyHook = fn;
}

/** Called by the editor once, with the catalog it already loads for the palette. */
export function setMergeFieldLabelLookup(fn: (name: string) => string | undefined): void {
  labelLookup = fn;
}

function fieldLabel(name: string): string {
  return labelLookup(name) ?? `{{${name}}}`;
}

/* ── registration ───────────────────────────────────────────────────────── */

export function registerDeltaBlots(Quill: any): void {
  if (registered) return;
  registered = true;

  const Embed = Quill.import('blots/embed');
  const BlockEmbed = Quill.import('blots/block/embed');

  /**
   * CALLOUT AS A LINE FORMAT, NOT AN OBJECT.
   *
   * A `ClassAttributor` at BLOCK scope makes `callout` behave exactly like
   * `header` or `list`: it rides on the newline that terminates the line, and
   * `getContents()` emits `{ insert: '\n', attributes: { callout: 'warning' } }`
   * — precisely what `DeltaLineAttributesSchema` now accepts.
   *
   * This is what makes an aside typeable. As a block EMBED it was one atomic
   * object; the browser would let an author type into the rendered box while
   * the Delta read its text back from a `data-content` attribute, so the edit
   * was displayed and then silently discarded on save. A line format has no
   * such second copy: the text in the editor IS the text in the document.
   */
  const Parchment = Quill.import('parchment');
  const CalloutClass = new Parchment.ClassAttributor('callout', 'dq-cl', {
    scope: Parchment.Scope.BLOCK,
    whitelist: ['info', 'warning', 'danger'],
  });
  Quill.register(CalloutClass, true);

  /* ── inline: a merge field ──────────────────────────────────────────── */
  class MergeFieldBlot extends Embed {
    static blotName = 'mergeField';
    static tagName = 'span';
    static className = 'dq-chip';

    static create(value: { name: string }) {
      const node: HTMLElement = super.create(value);
      const name = String(value?.name ?? '');
      node.classList.add(groupClassFor(name));
      node.setAttribute('data-field', name);
      // The token is the title so an author can always see what will resolve,
      // even though the chip reads as the label.
      node.setAttribute('title', `{{${name}}}`);
      node.textContent = fieldLabel(name);
      return node;
    }

    static value(node: HTMLElement) {
      return { name: node.getAttribute('data-field') ?? '' };
    }
  }

  /* ── inline: a cross-reference ──────────────────────────────────────── */
  class DocRefBlot extends Embed {
    static blotName = 'docRef';
    static tagName = 'span';
    static className = 'dq-xref';

    static create(value: { anchorId: string; text: string }) {
      const node: HTMLElement = super.create(value);
      node.setAttribute('data-anchor-id', String(value?.anchorId ?? ''));
      // `text` is the LAST KNOWN wording, and it is what the renderer falls
      // back to when the target heading has been deleted. Storing it on the
      // node is what keeps that fallback alive through an edit session.
      node.setAttribute('data-text', String(value?.text ?? ''));
      node.textContent = String(value?.text ?? '');
      return node;
    }

    static value(node: HTMLElement) {
      return {
        anchorId: node.getAttribute('data-anchor-id') ?? '',
        text: node.getAttribute('data-text') ?? node.textContent ?? '',
      };
    }
  }

  /* ── block: a page break ────────────────────────────────────────────── */
  class PageBreakBlot extends BlockEmbed {
    static blotName = 'pageBreak';
    static tagName = 'div';
    static className = 'dq-pagebreak';

    static create() {
      const node: HTMLElement = super.create();
      node.appendChild(el('span', undefined, 'Page break'));
      return node;
    }

    static value() {
      // The schema types this as the literal `true`, not an object.
      return true;
    }
  }

  /* ── block: a table ─────────────────────────────────────────────────
   *
   * EDITABLE CELLS, AND WHY THEY NEED A CALLBACK.
   *
   * A block embed is opaque to Quill: it never looks inside, and `value()` is
   * the only thing that decides what lands in the Delta. The first version of
   * this blot rendered plain `<td>`s that the browser happily let an author
   * type into while `value()` kept reading the ORIGINAL `data-table` — so a
   * filled-in table was displayed and then silently discarded on save.
   *
   * The fix has two halves and needs both:
   *   1. every cell edit writes straight back into `data-table`, which IS the
   *      value, so there is no second copy to drift; and
   *   2. `notifyTableEdit()` tells the editor a change happened, because Quill
   *      itself has no idea anything inside an embed moved and would leave the
   *      document looking saved.
   *
   * KNOWN LIMIT: cell edits are outside Quill's history, so undo does not
   * reach them. Losing an edit is worse than not being able to undo it, so
   * this is the right trade for now; a real fix needs a table structure Quill
   * models natively.
   */

  class TableBlot extends BlockEmbed {
    static blotName = 'table';
    static tagName = 'div';
    static className = 'dq-table';

    static create(value: { headers: string[]; rows: string[][] }) {
      const node: HTMLElement = super.create(value);
      const headers = Array.isArray(value?.headers) ? value.headers : [];
      const rows = Array.isArray(value?.rows) ? value.rows : [];
      node.setAttribute('data-table', JSON.stringify({ headers, rows }));

      const read = (): { headers: string[]; rows: string[][] } => {
        try {
          return JSON.parse(node.getAttribute('data-table') ?? '{"headers":[],"rows":[]}');
        } catch {
          return { headers: [], rows: [] };
        }
      };
      /**
       * Attribute-only, so the caret stays where the author is typing.
       * Committing through Quill on every keystroke would recreate the whole
       * embed and throw focus out of the cell after each character.
       */
      const stage = (next: { headers: string[]; rows: string[][] }) => {
        node.setAttribute('data-table', JSON.stringify(next));
      };

      /** Commit to Quill's model — on blur, and on any structural change. */
      const commit = (next: { headers: string[]; rows: string[][] }) => {
        stage(next);
        tableApplyHook(node, next);
      };

      const cell = (
        tag: 'th' | 'td', text: string, onInput: (v: string) => void,
      ): HTMLElement => {
        const c = el(tag);
        c.textContent = text;
        c.setAttribute('contenteditable', 'true');
        // Quill must not try to manage the caret inside an embed.
        c.addEventListener('input', () => onInput(c.textContent ?? ''));
        // The model catches up when the author leaves the cell.
        c.addEventListener('blur', () => commit(read()));
        c.addEventListener('keydown', (e) => {
          // Enter inside a cell would otherwise escape into the document.
          if ((e as KeyboardEvent).key === 'Enter') e.preventDefault();
        });
        return c;
      };

      const table = el('table');
      const thead = el('thead');
      const htr = el('tr');
      headers.forEach((h, ci) => {
        htr.appendChild(cell('th', String(h), (v) => {
          const cur = read(); cur.headers[ci] = v; stage(cur);
        }));
      });
      thead.appendChild(htr);
      table.appendChild(thead);

      const tbody = el('tbody');
      rows.forEach((row, ri) => {
        const tr = el('tr');
        (Array.isArray(row) ? row : []).forEach((c, ci) => {
          tr.appendChild(cell('td', String(c), (v) => {
            const cur = read(); cur.rows[ri][ci] = v; stage(cur);
          }));
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      node.appendChild(table);

      /* ── row / column controls ─────────────────────────────────────── */
      const bar = el('div', 'dq-tbar');
      const mk = (label: string, title: string, fn: () => void) => {
        const b = el('button', 'dq-tbtn', label);
        b.setAttribute('type', 'button');
        b.setAttribute('title', title);
        b.setAttribute('contenteditable', 'false');
        b.addEventListener('mousedown', (e) => { e.preventDefault(); e.stopPropagation(); });
        b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); fn(); });
        return b;
      };

      /*
       * No hand-rebuilt DOM. `commit` replaces the embed through Quill, which
       * calls `create()` again with the new value — so the redrawn table and
       * the stored value cannot disagree, and the listeners are bound to the
       * live node rather than to a detached copy.
       */
      const rebuild = (next: { headers: string[]; rows: string[][] }) => commit(next);

      bar.appendChild(mk('+ Row', 'Add a row', () => {
        const cur = read();
        cur.rows.push(new Array(cur.headers.length).fill(''));
        rebuild(cur);
      }));
      bar.appendChild(mk('+ Col', 'Add a column', () => {
        const cur = read();
        cur.headers.push(`Column ${cur.headers.length + 1}`);
        cur.rows.forEach((r) => r.push(''));
        rebuild(cur);
      }));
      bar.appendChild(mk('− Row', 'Remove the last row', () => {
        const cur = read();
        if (cur.rows.length <= 1) return;
        cur.rows.pop();
        rebuild(cur);
      }));
      bar.appendChild(mk('− Col', 'Remove the last column', () => {
        const cur = read();
        if (cur.headers.length <= 1) return;
        cur.headers.pop();
        cur.rows.forEach((r) => r.pop());
        rebuild(cur);
      }));
      node.appendChild(bar);

      return node;
    }

    static value(node: HTMLElement) {
      try {
        return JSON.parse(node.getAttribute('data-table') ?? '{"headers":[],"rows":[]}');
      } catch {
        return { headers: [], rows: [] };
      }
    }
  }

  /* ── block: a signature panel ───────────────────────────────────────── */
  class SignatureFieldBlot extends BlockEmbed {
    static blotName = 'signatureField';
    static tagName = 'div';
    static className = 'dq-sig';

    static create(value: any) {
      const node: HTMLElement = super.create(value);
      node.setAttribute('data-sig', JSON.stringify(value ?? {}));

      node.appendChild(el('div', 'dq-sig-cap', String(value?.label ?? value?.signerRole ?? 'Signature')));
      const grid = el('div', 'dq-sig-grid');

      const cell = (label: string) => {
        const c = el('div');
        c.appendChild(el('div', 'dq-sig-line'));
        c.appendChild(el('div', 'dq-sig-lab', label));
        return c;
      };

      grid.appendChild(cell('Signature'));
      // Each line is drawn ONLY when its flag is on. The flags carry no
      // default here, matching the schema: a stored panel that never set
      // `showName` renders no printed-name line, and inventing one would add
      // a line to a contract nobody asked for.
      if (value?.showDate) grid.appendChild(cell(String(value?.dateLabel ?? 'Date')));
      if (value?.showName) grid.appendChild(cell(String(value?.nameLabel ?? 'Printed name')));
      if (value?.showTitle) grid.appendChild(cell(String(value?.titleLabel ?? 'Title')));
      node.appendChild(grid);
      return node;
    }

    static value(node: HTMLElement) {
      try {
        return JSON.parse(node.getAttribute('data-sig') ?? '{}');
      } catch {
        return { signerRole: 'signer' };
      }
    }
  }

  /* ── block: a labelled merge-field line ─────────────────────────────── */
  class MergeFieldBlockBlot extends BlockEmbed {
    static blotName = 'mergeFieldBlock';
    static tagName = 'div';
    static className = 'dq-mfline';

    static create(value: { name: string; label?: string }) {
      const node: HTMLElement = super.create(value);
      const name = String(value?.name ?? '');
      node.setAttribute('data-field', name);
      if (value?.label) node.setAttribute('data-label', String(value.label));
      if (value?.label) node.appendChild(el('span', 'dq-mflabel', `${value.label}:`));
      const chip = el('span', `dq-chip ${groupClassFor(name)}`, fieldLabel(name));
      chip.setAttribute('data-field', name);
      node.appendChild(chip);
      return node;
    }

    static value(node: HTMLElement) {
      const label = node.getAttribute('data-label');
      const name = node.getAttribute('data-field') ?? '';
      return label ? { name, label } : { name };
    }
  }

  /* ── block: a schedule ──────────────────────────────────────────────── */
  /**
   * A SCHEDULE IS NOT AN EDITABLE TABLE.
   *
   * Its rows come from the ORDER, not from the author, so unlike `TableBlot`
   * nothing inside it is contenteditable — there is no cell to type into. What
   * the author sees is the shape they configured: the title, the column
   * headings, and one ghost row saying where the real rows come from.
   *
   * Reconfiguring goes back through the insert dialog rather than being edited
   * in place, so there is exactly one code path that can produce a schedule
   * config and it is the one that validates it.
   */
  class ScheduleTableBlot extends BlockEmbed {
    static blotName = 'scheduleTable';
    static tagName = 'div';
    static className = 'dq-schedule';

    static create(value: {
      source: string; columns: { key: string; label: string }[];
      title?: string; allowEmpty?: boolean;
    }) {
      const node: HTMLElement = super.create(value);
      const columns = Array.isArray(value?.columns) ? value.columns : [];
      const source = String(value?.source ?? 'equipment');
      node.setAttribute('data-schedule', JSON.stringify({
        source,
        columns,
        ...(value?.title ? { title: value.title } : {}),
        ...(value?.allowEmpty ? { allowEmpty: true } : {}),
      }));
      node.setAttribute('contenteditable', 'false');

      if (value?.title) node.appendChild(el('div', 'dq-schedule-title', String(value.title)));

      const table = el('table', 'dq-schedule-table');
      const thead = el('thead');
      const hrow = el('tr');
      for (const c of columns) hrow.appendChild(el('th', undefined, c.label));
      thead.appendChild(hrow);
      table.appendChild(thead);

      const tbody = el('tbody');
      const ghost = el('tr', 'dq-schedule-ghost');
      for (let i = 0; i < Math.max(columns.length, 1); i += 1) {
        ghost.appendChild(el('td', undefined, i === 0
          ? `one row per ${source === 'packages' ? 'package' : 'device'} on the order`
          : '…'));
      }
      tbody.appendChild(ghost);
      table.appendChild(tbody);
      node.appendChild(table);

      if (value?.allowEmpty) {
        node.appendChild(el('div', 'dq-schedule-note', 'Optional — may render with no rows'));
      }
      return node;
    }

    static value(node: HTMLElement) {
      try {
        const v = JSON.parse(node.getAttribute('data-schedule') ?? '{}');
        return {
          source: v.source,
          columns: Array.isArray(v.columns) ? v.columns : [],
          ...(v.title ? { title: v.title } : {}),
          ...(v.allowEmpty ? { allowEmpty: true } : {}),
        };
      } catch {
        return { source: 'equipment', columns: [] };
      }
    }
  }

  Quill.register(MergeFieldBlot);
  Quill.register(DocRefBlot);
  Quill.register(PageBreakBlot);
  Quill.register(TableBlot);
  Quill.register(SignatureFieldBlot);
  Quill.register(MergeFieldBlockBlot);
  Quill.register(ScheduleTableBlot);
}

/**
 * THE CLOSED FORMAT SET, and the reason it exists.
 *
 * Quill will happily emit `color`, `background`, `font`, `size`, `script`,
 * `code-block`, `blockquote`, `indent` and `direction`. Neither renderer can
 * draw most of those, and an attribute the renderer silently ignores is worse
 * than one it rejects: the author sees their formatting in the editor, it
 * vanishes from the executed PDF, and nothing anywhere said so.
 *
 * `DeltaDocumentSchema` is `.strict()` and would reject them at save time.
 * This list is the matching restriction at the INPUT, so the two never drift
 * and the author is never allowed to apply a format that cannot survive.
 */
export const DELTA_FORMATS: readonly string[] = [
  'bold', 'italic', 'underline', 'strike', 'link',
  'header', 'list', 'align', 'callout',
  'mergeField', 'docRef', 'pageBreak',
  'callout', 'table', 'signatureField', 'mergeFieldBlock', 'scheduleTable',
];
